import { useState } from 'react';

export default function Registration() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    position: '',
    participationType: 'حضور',
    interests: [] as string[]
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('organization', formData.organization);
      formBody.append('position', formData.position);
      formBody.append('participationType', formData.participationType);
      formBody.append('interests', formData.interests.join(', '));

      const response = await fetch('https://formspree.io/f/mwvvvpkk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          organization: '',
          position: '',
          participationType: 'حضور',
          interests: []
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInterestToggle = (interest: string) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  return (
    <section id="registration" className="py-10 md:py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl">
          <div className="relative bg-[#F5F5DC] p-8 md:p-12 lg:p-16 flex flex-col justify-center min-h-[300px] lg:min-h-0">
            <div className="absolute inset-0 opacity-10">
              <img
                src="https://readdy.ai/api/search-image?query=abstract%20geometric%20pattern%20saudi%20arabian%20islamic%20art%20motifs%20modern%20minimalist%20design%20green%20gold%20colors%20elegant%20cultural%20heritage%20contemporary%20style&width=800&height=1000&seq=reg-bg-001&orientation=portrait"
                alt="Background"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10 space-y-6 md:space-y-8">
              <div className="space-y-3 md:space-y-4">
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1B5E20] leading-tight">
                  سجل
                  <span className="block text-3xl md:text-4xl lg:text-5xl mt-2">مشاركتك</span>
                  <span className="block text-5xl md:text-6xl lg:text-7xl mt-2">الآن</span>
                </h2>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 md:p-12 lg:p-16">
            <div className="mb-8 md:mb-12 text-center">
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#1B5E20] mb-3 md:mb-4">
                ابدأ رحلتك في صناعة المستقبل
              </h3>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                انضم إلى نخبة من الشباب والخبراء في ملتقى وطني يهدف لتشخيص احتياجات الشباب وبناء مستقبل أفضل
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6" data-readdy-form id="registration-form">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">الاسم الكامل *</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border border-gray-300 focus:border-[#1B5E20] focus:ring-2 focus:ring-[#1B5E20]/20 outline-none transition-all text-sm md:text-base"
                  placeholder="أدخل اسمك الكامل"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">البريد الإلكتروني *</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border border-gray-300 focus:border-[#1B5E20] focus:ring-2 focus:ring-[#1B5E20]/20 outline-none transition-all text-sm md:text-base"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">رقم الجوال *</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border border-gray-300 focus:border-[#1B5E20] focus:ring-2 focus:ring-[#1B5E20]/20 outline-none transition-all text-sm md:text-base"
                  placeholder="05xxxxxxxx"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">الجهة / المؤسسة</label>
                <input
                  type="text"
                  name="organization"
                  value={formData.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border border-gray-300 focus:border-[#1B5E20] focus:ring-2 focus:ring-[#1B5E20]/20 outline-none transition-all text-sm md:text-base"
                  placeholder="اسم الجهة أو المؤسسة"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">المسمى الوظيفي</label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border border-gray-300 focus:border-[#1B5E20] focus:ring-2 focus:ring-[#1B5E20]/20 outline-none transition-all text-sm md:text-base"
                  placeholder="المسمى الوظيفي"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">نوع المشاركة *</label>
                <select
                  name="participationType"
                  required
                  value={formData.participationType}
                  onChange={(e) => setFormData({ ...formData, participationType: e.target.value })}
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border border-gray-300 focus:border-[#1B5E20] focus:ring-2 focus:ring-[#1B5E20]/20 outline-none transition-all text-sm md:text-base cursor-pointer"
                >
                  <option value="حضور">حضور</option>
                  <option value="متحدث">متحدث</option>
                  <option value="عارض">عارض في المعرض</option>
                  <option value="متطوع">متطوع</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-3">المجالات المهتم بها</label>
                <div className="grid grid-cols-2 gap-2 md:gap-3">
                  {['التعليم', 'التوظيف', 'القيم', 'الصحة', 'الابتكار', 'المشاركة المجتمعية'].map((interest) => (
                    <button
                      key={interest}
                      type="button"
                      onClick={() => handleInterestToggle(interest)}
                      className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                        formData.interests.includes(interest)
                          ? 'bg-[#1B5E20] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {interest}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 md:px-8 py-3 md:py-4 bg-[#1B5E20] text-white rounded-full font-bold text-base md:text-lg hover:bg-[#2E7D32] transition-all duration-300 flex items-center justify-center gap-2 md:gap-3 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'جاري التسجيل...' : 'تأكيد التسجيل'}
                <i className="ri-send-plane-fill text-lg md:text-xl"></i>
              </button>

              {submitStatus === 'success' && (
                <div className="p-3 md:p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-center">
                  <i className="ri-checkbox-circle-line text-xl md:text-2xl mb-2"></i>
                  <p className="font-medium text-sm md:text-base">تم التسجيل بنجاح! سنتواصل معك قريباً</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-3 md:p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-center">
                  <i className="ri-error-warning-line text-xl md:text-2xl mb-2"></i>
                  <p className="font-medium text-sm md:text-base">حدث خطأ، يرجى المحاولة مرة أخرى</p>
                </div>
              )}
            </form>

            <div className="mt-6 md:mt-8 pt-6 md:pt-8 border-t border-gray-200">
              <h4 className="text-base md:text-lg font-bold text-gray-800 mb-3 md:mb-4">مميزات المشاركة</h4>
              <div className="space-y-2 md:space-y-3">
                <div className="flex items-center gap-2 md:gap-3">
                  <i className="ri-medal-line text-lg md:text-xl text-[#D4AF37]"></i>
                  <span className="text-xs md:text-sm text-gray-600">شهادة حضور معتمدة</span>
                </div>
                <div className="flex items-center gap-2 md:gap-3">
                  <i className="ri-star-line text-lg md:text-xl text-[#D4AF37]"></i>
                  <span className="text-xs md:text-sm text-gray-600">أولوية المشاركة في المبادرات الناتجة</span>
                </div>
                <div className="flex items-center gap-2 md:gap-3">
                  <i className="ri-team-line text-lg md:text-xl text-[#D4AF37]"></i>
                  <span className="text-xs md:text-sm text-gray-600">شبكة علاقات وطنية</span>
                </div>
                <div className="flex items-center gap-2 md:gap-3">
                  <i className="ri-file-list-line text-lg md:text-xl text-[#D4AF37]"></i>
                  <span className="text-xs md:text-sm text-gray-600">نشر الأسماء في التقرير الختامي</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
